import { DomainList, DomainListItemCard } from "@/components/domain";
import { Badge } from "@/components/ui/badge";
import { useDeleteRegion, useGetRegions } from "@/hooks/regions";
import { T } from "@/i18n";
import { cn } from "@/lib/utils";
import { Region } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/(regions)/regions")({
  component: RouteComponent,
});

function RouteComponent() {
  const query = useGetRegions();

  return (
    <DomainList
      domain="regions"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="regions.title" /> <MapPin width={16} height={16} />
            </>
          ),
          href: "/regions",
        },
      ]}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "regions.fields.name",
          },
          {
            header: "regions.fields.description",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/regions/new"
      renderItem={(region, index, isTable) => (
        <RegionCard region={region} key={index} isTable={isTable} />
      )}
    />
  );
}

function RegionCard({ region, isTable }: { region: Region; isTable: boolean }) {
  const { mutateAsync } = useDeleteRegion();
  const deleteRegion = () => mutateAsync(region.id.toString());
  const { t } = useTranslation();

  const regionColor = useMemo(() => {
    if (!region.color && !isTable) return <></>;
    return (
      <Badge
        className={cn(
          "h-5 w-14 font-bold",
          !isTable && "absolute left-4 top-0 -translate-y-1/2",
          !region.color && "border-border bg-transparent",
        )}
        style={{ backgroundColor: region.color ?? undefined }}
      />
    );
  }, [isTable, region.color]);
  return (
    <DomainListItemCard
      domain="regions"
      table={isTable}
      rowClassName="relative"
      style={{ borderColor: region.color ?? undefined }}
      className="relative"
      title={
        !isTable ? (
          region.name
        ) : (
          <div className="flex items-center gap-2">
            {regionColor}
            {region.name}{" "}
          </div>
        )
      }
      viewLink={(children, key) => (
        <Link key={key} to="/regions/$id" params={{ id: region.id.toString() }}>
          {children}
        </Link>
      )}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/regions/$id/edit"
          params={{ id: region.id.toString() }}
        >
          {children}
        </Link>
      )}
      contentMap={[
        {
          label: t("regions.fields.description"),
          value: region.description || "-",
        },
      ]}
      onDelete={deleteRegion}
    >
      {!isTable ? regionColor : <></>}
    </DomainListItemCard>
  );
}
